import { z } from "zod";
import { authHeadersSchema, initContract } from "./base";
import { apiErrorSchema } from "./errors";
import { adAttributionMetadataSchema } from "./zero-attribution";

const c = initContract();

// ---------------------------------------------------------------------------
// Response schemas
// ---------------------------------------------------------------------------

const autoRechargeSchema = z.object({
  enabled: z.boolean(),
  threshold: z.number().nullable(),
  amount: z.number().nullable(),
});

const creditExpirySchema = z.object({
  expiringNextCycle: z.number(),
  nextExpiryDate: z.string().nullable(),
});

const creditBreakdownSegmentSchema = z.object({
  category: z.enum(["plan", "free", "promotional", "payAsYouGo"]),
  label: z.string(),
  credits: z.number(),
  // Only set on `plan` segments. Lets the UI decide whether a segment
  // represents the current plan or leftover credits from a previous plan
  // without round-tripping through the `label` string.
  tier: z.enum(["pro", "team"]).optional(),
});

const creditGrantSchema = z.object({
  id: z.string(),
  source: z.string(),
  label: z.string(),
  amount: z.number(),
  remaining: z.number(),
  createdAt: z.string(),
  expiresAt: z.string(),
});

const scheduledBillingChangeSchema = z.object({
  type: z.enum(["cancel", "downgrade"]),
  targetTier: z
    .enum(["limited-free-1", "pro-suspend", "pro", "team"])
    .nullable(),
  effectiveDate: z.string().nullable(),
});

const concurrencySubscriptionSchema = z.object({
  id: z.string(),
  quantity: z.number().int().nonnegative(),
  currentPeriodEnd: z.string().nullable(),
  cancelAtPeriodEnd: z.boolean(),
});

const usageAllowanceWindowSchema = z.object({
  kind: z.enum(["short", "weekly"]),
  windowSeconds: z.number().int().positive(),
  unitLimit: z.number(),
  consumedUnits: z.number(),
  remainingUnits: z.number(),
  startsAt: z.string().nullable(),
  expiresAt: z.string().nullable(),
});

const usageAllowanceSchema = z.object({
  windows: z.array(usageAllowanceWindowSchema),
});

const billingStatusResponseSchema = z.object({
  tier: z.string(),
  canBuyConcurrency: z.boolean().optional(),
  canBuyCredits: z.boolean().optional(),
  autoRechargeAllowed: z.boolean().optional(),
  supportByok: z.boolean().optional(),
  restrictedVm0Models: z.boolean().optional(),
  videoGenerationAllowed: z.boolean().optional(),
  workflowWebhookAutomationAllowed: z.boolean().optional(),
  credits: z.number(),
  onboardingPaymentPending: z.boolean(),
  subscriptionStatus: z.string().nullable(),
  currentPeriodEnd: z.string().nullable(),
  cancelAtPeriodEnd: z.boolean(),
  scheduledChange: scheduledBillingChangeSchema.nullable(),
  hasSubscription: z.boolean(),
  autoRecharge: autoRechargeSchema,
  creditExpiry: creditExpirySchema,
  creditBreakdown: z.array(creditBreakdownSegmentSchema),
  creditGrants: z.array(creditGrantSchema),
  concurrencyLimit: z.number().int().nonnegative(),
  concurrencySubscriptions: z.array(concurrencySubscriptionSchema),
  usageAllowance: usageAllowanceSchema.nullable().optional(),
});

const checkoutResponseSchema = z.object({
  url: z.string(),
});

const checkoutCompleteResponseSchema = z.object({
  completed: z.boolean(),
});

const redeemCodeResponseSchema = z.object({
  redeemed: z.literal(true),
});

const portalResponseSchema = z.object({
  url: z.string(),
});

const redeemResponseSchema = z.discriminatedUnion("status", [
  z.object({
    status: z.literal("ready"),
    checkoutUrl: z.string().url(),
  }),
  z.object({
    status: z.literal("already_granted"),
  }),
  z.object({
    status: z.literal("processing"),
  }),
  z.object({
    status: z.literal("error"),
    reason: z.enum([
      "campaign_misconfigured",
      "admin_required",
      "billing_unavailable",
    ]),
  }),
]);

// ---------------------------------------------------------------------------
// Request schemas
// ---------------------------------------------------------------------------

const checkoutRequestSchema = z.object({
  tier: z.enum(["pro", "team"]),
  successUrl: z.string().url(),
  cancelUrl: z.string().url(),
  trialDays: z.literal(7).optional(),
  adAttribution: adAttributionMetadataSchema.optional(),
});

const checkoutCompleteRequestSchema = z.object({
  sessionId: z.string().min(1),
});

const concurrencyCheckoutRequestSchema = z.object({
  quantity: z.number().int().min(1).max(1000),
  successUrl: z.string().url(),
  cancelUrl: z.string().url(),
});

const concurrencySubscriptionCancelResponseSchema = z.object({
  success: z.literal(true),
  currentPeriodEnd: z.string().nullable(),
});

const concurrencySubscriptionRestoreResponseSchema = z.object({
  success: z.literal(true),
});

const creditCheckoutRequestSchema = z
  .object({
    credits: z.number().int().min(1000).max(10_000_000),
    customAmount: z.boolean().optional(),
    successUrl: z.string().url(),
    cancelUrl: z.string().url(),
    autoRecharge: z
      .object({
        enabled: z.boolean(),
        threshold: z.number().int().positive().max(10_000_000).optional(),
        amount: z.number().int().min(1000).max(10_000_000).optional(),
      })
      .optional(),
  })
  .refine(
    (data) => {
      return data.customAmount !== true || data.autoRecharge === undefined;
    },
    { message: "auto-recharge is not supported for custom amount checkout" },
  )
  .refine(
    (data) => {
      return (
        data.autoRecharge?.enabled !== true ||
        data.autoRecharge.threshold === undefined ||
        data.autoRecharge.amount === undefined ||
        data.autoRecharge.threshold < data.autoRecharge.amount
      );
    },
    { message: "threshold must be less than amount to avoid recharge loops" },
  );

const portalRequestSchema = z.object({
  returnUrl: z.string().url(),
});

const autoRechargeUpdateRequestSchema = z
  .object({
    enabled: z.boolean(),
    threshold: z.number().int().positive().max(10_000_000).optional(),
    amount: z.number().int().min(1000).max(10_000_000).optional(),
  })
  .refine(
    (data) => {
      return (
        !data.enabled ||
        data.threshold === undefined ||
        data.amount === undefined ||
        data.threshold < data.amount
      );
    },
    { message: "threshold must be less than amount to avoid recharge loops" },
  );

const redeemRequestSchema = z.object({
  successUrl: z.string().url(),
  cancelUrl: z.string().url(),
});

const redeemCodeRequestSchema = z.object({
  code: z.string().trim().min(1),
});

// ---------------------------------------------------------------------------
// Contracts
// ---------------------------------------------------------------------------

/**
 * Zero contract for GET /api/zero/billing/status
 */
export const zeroBillingStatusContract = c.router({
  get: {
    method: "GET",
    path: "/api/zero/billing/status",
    headers: authHeadersSchema,
    responses: {
      200: billingStatusResponseSchema,
      401: apiErrorSchema,
      403: apiErrorSchema,
      500: apiErrorSchema,
    },
    summary: "Get billing status for current org",
  },
});

export type ZeroBillingStatusContract = typeof zeroBillingStatusContract;

/**
 * Zero contract for POST /api/zero/billing/checkout
 */
export const zeroBillingCheckoutContract = c.router({
  create: {
    method: "POST",
    path: "/api/zero/billing/checkout",
    headers: authHeadersSchema,
    body: checkoutRequestSchema,
    responses: {
      200: checkoutResponseSchema,
      400: apiErrorSchema,
      401: apiErrorSchema,
      403: apiErrorSchema,
      500: apiErrorSchema,
      503: apiErrorSchema,
    },
    summary: "Create Stripe checkout session",
  },
  complete: {
    method: "POST",
    path: "/api/zero/billing/checkout/complete",
    headers: authHeadersSchema,
    body: checkoutCompleteRequestSchema,
    responses: {
      200: checkoutCompleteResponseSchema,
      400: apiErrorSchema,
      401: apiErrorSchema,
      403: apiErrorSchema,
      500: apiErrorSchema,
      503: apiErrorSchema,
    },
    summary: "Complete Stripe checkout session",
  },
});

export type ZeroBillingCheckoutContract = typeof zeroBillingCheckoutContract;

/**
 * Zero contract for POST /api/zero/billing/concurrency-checkout
 */
export const zeroBillingConcurrencyCheckoutContract = c.router({
  create: {
    method: "POST",
    path: "/api/zero/billing/concurrency-checkout",
    headers: authHeadersSchema,
    body: concurrencyCheckoutRequestSchema,
    responses: {
      200: checkoutResponseSchema,
      400: apiErrorSchema,
      401: apiErrorSchema,
      403: apiErrorSchema,
      500: apiErrorSchema,
      503: apiErrorSchema,
    },
    summary: "Create Stripe checkout session for concurrency add-on",
  },
});

export type ZeroBillingConcurrencyCheckoutContract =
  typeof zeroBillingConcurrencyCheckoutContract;

/**
 * Zero contract for concurrency subscriptions.
 */
export const zeroBillingConcurrencySubscriptionContract = c.router({
  cancel: {
    method: "POST",
    path: "/api/zero/billing/concurrency-subscriptions/:subscriptionId/cancel",
    pathParams: z.object({
      subscriptionId: z.string().min(1),
    }),
    headers: authHeadersSchema,
    body: z.object({}),
    responses: {
      200: concurrencySubscriptionCancelResponseSchema,
      401: apiErrorSchema,
      403: apiErrorSchema,
      404: apiErrorSchema,
      500: apiErrorSchema,
      503: apiErrorSchema,
    },
    summary: "Cancel a concurrency add-on subscription at period end",
  },
  restore: {
    method: "POST",
    path: "/api/zero/billing/concurrency-subscriptions/:subscriptionId/restore",
    pathParams: z.object({
      subscriptionId: z.string().min(1),
    }),
    headers: authHeadersSchema,
    body: z.object({}),
    responses: {
      200: concurrencySubscriptionRestoreResponseSchema,
      401: apiErrorSchema,
      403: apiErrorSchema,
      404: apiErrorSchema,
      500: apiErrorSchema,
      503: apiErrorSchema,
    },
    summary: "Restore a concurrency add-on subscription",
  },
});

export type ZeroBillingConcurrencySubscriptionContract =
  typeof zeroBillingConcurrencySubscriptionContract;

/**
 * Zero contract for POST /api/zero/billing/credit-checkout
 */
export const zeroBillingCreditCheckoutContract = c.router({
  create: {
    method: "POST",
    path: "/api/zero/billing/credit-checkout",
    headers: authHeadersSchema,
    body: creditCheckoutRequestSchema,
    responses: {
      200: checkoutResponseSchema,
      400: apiErrorSchema,
      401: apiErrorSchema,
      403: apiErrorSchema,
      500: apiErrorSchema,
      503: apiErrorSchema,
    },
    summary: "Create Stripe checkout session for credits",
  },
});

export type ZeroBillingCreditCheckoutContract =
  typeof zeroBillingCreditCheckoutContract;

/**
 * Zero contract for POST /api/zero/billing/portal
 */
export const zeroBillingPortalContract = c.router({
  create: {
    method: "POST",
    path: "/api/zero/billing/portal",
    headers: authHeadersSchema,
    body: portalRequestSchema,
    responses: {
      200: portalResponseSchema,
      400: apiErrorSchema,
      401: apiErrorSchema,
      403: apiErrorSchema,
      500: apiErrorSchema,
      503: apiErrorSchema,
    },
    summary: "Create Stripe billing portal session",
  },
});

export type ZeroBillingPortalContract = typeof zeroBillingPortalContract;

/**
 * Zero contract for /api/zero/billing/auto-recharge
 */
export const zeroBillingAutoRechargeContract = c.router({
  get: {
    method: "GET",
    path: "/api/zero/billing/auto-recharge",
    headers: authHeadersSchema,
    responses: {
      200: autoRechargeSchema,
      401: apiErrorSchema,
      500: apiErrorSchema,
    },
    summary: "Get auto-recharge configuration",
  },
  update: {
    method: "PUT",
    path: "/api/zero/billing/auto-recharge",
    headers: authHeadersSchema,
    body: autoRechargeUpdateRequestSchema,
    responses: {
      200: autoRechargeSchema,
      400: apiErrorSchema,
      401: apiErrorSchema,
      403: apiErrorSchema,
      500: apiErrorSchema,
    },
    summary: "Update auto-recharge configuration",
  },
});

export type ZeroBillingAutoRechargeContract =
  typeof zeroBillingAutoRechargeContract;

/**
 * Zero contract for GET /api/zero/billing/invoices
 */
const invoiceSchema = z.object({
  id: z.string(),
  number: z.string().nullable(),
  date: z.number(),
  amount: z.number(),
  status: z.string().nullable(),
  hostedInvoiceUrl: z.string().nullable(),
});

const billingInvoicesResponseSchema = z.object({
  invoices: z.array(invoiceSchema),
  // Optional while the frontend can overlap with API deployments that do not expose ZIP downloads yet.
  receiptDownloadsSupported: z.literal(true).optional(),
});

const billingReceiptsMonthSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/u);

function billingMonthIndex(month: string): number {
  return Number(month.slice(0, 4)) * 12 + Number(month.slice(5, 7)) - 1;
}

export const zeroBillingInvoicesContract = c.router({
  get: {
    method: "GET",
    path: "/api/zero/billing/invoices",
    headers: authHeadersSchema,
    responses: {
      200: billingInvoicesResponseSchema,
      401: apiErrorSchema,
      403: apiErrorSchema,
      500: apiErrorSchema,
    },
    summary: "Get invoices for current org",
  },
  downloadReceipts: {
    method: "GET",
    path: "/api/zero/billing/invoices/receipts",
    headers: authHeadersSchema,
    query: z
      .object({
        startMonth: billingReceiptsMonthSchema,
        endMonth: billingReceiptsMonthSchema,
      })
      .refine((query) => {
        return query.startMonth <= query.endMonth;
      }, "startMonth must not be after endMonth")
      .refine((query) => {
        return (
          billingMonthIndex(query.endMonth) -
            billingMonthIndex(query.startMonth) <
          3
        );
      }, "Receipt downloads are limited to three consecutive months"),
    responses: {
      200: c.otherResponse({
        contentType: "application/zip",
        body: c.type<Blob>(),
      }),
      401: apiErrorSchema,
      403: apiErrorSchema,
      404: apiErrorSchema,
      500: apiErrorSchema,
      502: apiErrorSchema,
    },
    summary: "Download monthly receipts for current org",
  },
});

export type ZeroBillingInvoicesContract = typeof zeroBillingInvoicesContract;

// ---------------------------------------------------------------------------
// Downgrade
// ---------------------------------------------------------------------------

const downgradeRequestSchema = z.object({
  targetTier: z.enum(["limited-free-1", "pro-suspend", "pro"]),
  returnUrl: z.string().url().optional(),
});

const downgradeResponseSchema = z.union([
  z.object({
    success: z.boolean(),
    effectiveDate: z.string().nullable(),
  }),
  z.object({
    status: z.literal("payment_method_required"),
    checkoutUrl: z.string().url(),
  }),
]);

const restoreRequestSchema = z.object({
  returnUrl: z.string().url().optional(),
});

const restoreResponseSchema = z.discriminatedUnion("status", [
  z.object({
    status: z.literal("restored"),
  }),
  z.object({
    status: z.literal("payment_method_required"),
    checkoutUrl: z.string().url(),
  }),
]);

/**
 * Zero contract for POST /api/zero/billing/downgrade
 */
export const zeroBillingDowngradeContract = c.router({
  create: {
    method: "POST",
    path: "/api/zero/billing/downgrade",
    headers: authHeadersSchema,
    body: downgradeRequestSchema,
    responses: {
      200: downgradeResponseSchema,
      400: apiErrorSchema,
      401: apiErrorSchema,
      403: apiErrorSchema,
      409: apiErrorSchema,
      500: apiErrorSchema,
      503: apiErrorSchema,
    },
    summary: "Downgrade subscription to a lower tier",
  },
});

export type ZeroBillingDowngradeContract = typeof zeroBillingDowngradeContract;

/**
 * Zero contract for POST /api/zero/billing/restore
 */
export const zeroBillingRestoreContract = c.router({
  create: {
    method: "POST",
    path: "/api/zero/billing/restore",
    headers: authHeadersSchema,
    body: restoreRequestSchema,
    responses: {
      200: restoreResponseSchema,
      401: apiErrorSchema,
      403: apiErrorSchema,
      409: apiErrorSchema,
      500: apiErrorSchema,
      503: apiErrorSchema,
    },
    summary: "Restore a subscription scheduled for cancellation",
  },
});

export type ZeroBillingRestoreContract = typeof zeroBillingRestoreContract;

/**
 * Zero contract for POST /api/zero/billing/redeem/:campaign
 *
 * One-time campaign redemption. The handler validates the campaign whitelist,
 * creates (or resumes) a Stripe Checkout session, and returns a discriminated
 * union so a single landing page on the platform can render the appropriate
 * state (ready / already_granted / processing / error).
 */
export const zeroBillingRedeemContract = c.router({
  create: {
    method: "POST",
    path: "/api/zero/billing/redeem/:campaign",
    pathParams: z.object({
      campaign: z.string(),
    }),
    headers: authHeadersSchema,
    body: redeemRequestSchema,
    responses: {
      200: redeemResponseSchema,
      400: apiErrorSchema,
      401: apiErrorSchema,
      500: apiErrorSchema,
    },
    summary: "Redeem a one-time campaign",
  },
});

export type ZeroBillingRedeemContract = typeof zeroBillingRedeemContract;

/**
 * Zero contract for POST /api/zero/billing/redeem-code
 *
 * Proxies onboarding invite codes to the Atom redeem endpoint. The platform
 * waits for billing status to update through realtime before completing
 * onboarding.
 */
export const zeroBillingRedeemCodeContract = c.router({
  create: {
    method: "POST",
    path: "/api/zero/billing/redeem-code",
    headers: authHeadersSchema,
    body: redeemCodeRequestSchema,
    responses: {
      200: redeemCodeResponseSchema,
      400: apiErrorSchema,
      401: apiErrorSchema,
      403: apiErrorSchema,
      500: apiErrorSchema,
      503: apiErrorSchema,
    },
    summary: "Redeem an onboarding billing code",
  },
});

export type ZeroBillingRedeemCodeContract =
  typeof zeroBillingRedeemCodeContract;

// Inferred types from Zod schemas
export type BillingStatusResponse = z.infer<typeof billingStatusResponseSchema>;
export type AutoRechargeConfig = z.infer<typeof autoRechargeSchema>;
export type CheckoutResponse = z.infer<typeof checkoutResponseSchema>;
export type RedeemCodeResponse = z.infer<typeof redeemCodeResponseSchema>;
export type ConcurrencyCheckoutRequest = z.infer<
  typeof concurrencyCheckoutRequestSchema
>;
export type CreditCheckoutRequest = z.infer<typeof creditCheckoutRequestSchema>;
export type PortalResponse = z.infer<typeof portalResponseSchema>;
export type BillingInvoice = z.infer<typeof invoiceSchema>;
export type BillingInvoicesResponse = z.infer<
  typeof billingInvoicesResponseSchema
>;
export type DowngradeResponse = z.infer<typeof downgradeResponseSchema>;
export type RestoreResponse = z.infer<typeof restoreResponseSchema>;
export type RedeemRequest = z.infer<typeof redeemRequestSchema>;
export type RedeemCodeRequest = z.infer<typeof redeemCodeRequestSchema>;
export type RedeemResponse = z.infer<typeof redeemResponseSchema>;
